import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { tapScale } from '@/lib/motion'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  pageSizeOptions?: number[]
  showPageSize?: boolean
  showInfo?: boolean
  className?: string
}

export function Pagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
  showPageSize = true,
  showInfo = true,
  className,
}: PaginationProps) {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalItems)

  const canGoPrev = currentPage > 1
  const canGoNext = currentPage < totalPages

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Page Size Selector & Info */}
      {(showPageSize || showInfo) && (
        <div className="flex items-center justify-between text-xs">
          {showPageSize && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">每頁</span>
              <div className="flex items-center gap-1">
                {pageSizeOptions.map((size) => (
                  <motion.button
                    key={size}
                    whileTap={tapScale}
                    onClick={() => onPageSizeChange(size)}
                    className={cn(
                      "h-7 min-w-[32px] px-2 rounded-lg text-xs font-medium transition-all",
                      pageSize === size
                        ? "bg-gold text-background shadow-glow-gold"
                        : "glass-card hover:border-gold/30 text-muted-foreground hover:text-gold"
                    )}
                  >
                    {size}
                  </motion.button>
                ))}
              </div>
              <span className="text-muted-foreground">筆</span>
            </div>
          )}
          {showInfo && (
            <span className="text-muted-foreground">
              {totalItems > 0 ? (
                <><span className="text-foreground font-medium">{startItem}-{endItem}</span> / <span className="text-foreground font-medium">{totalItems}</span></>
              ) : (
                '無資料'
              )}
            </span>
          )}
        </div>
      )}

      {/* Navigation */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1">
          {/* First */}
          <motion.button
            whileTap={canGoPrev ? tapScale : undefined}
            onClick={() => canGoPrev && onPageChange(1)}
            disabled={!canGoPrev}
            className={cn(
              "w-9 h-9 rounded-xl flex items-center justify-center transition-all",
              canGoPrev
                ? "glass-card hover:border-gold/30 text-muted-foreground hover:text-gold"
                : "bg-white/5 text-white/20 cursor-not-allowed"
            )}
            aria-label="第一頁"
          >
            <ChevronsLeft className="w-4 h-4" />
          </motion.button>

          {/* Previous */}
          <motion.button
            whileTap={canGoPrev ? tapScale : undefined}
            onClick={() => canGoPrev && onPageChange(currentPage - 1)}
            disabled={!canGoPrev}
            className={cn(
              "w-9 h-9 rounded-xl flex items-center justify-center transition-all",
              canGoPrev
                ? "glass-card hover:border-gold/30 text-muted-foreground hover:text-gold"
                : "bg-white/5 text-white/20 cursor-not-allowed"
            )}
            aria-label="上一頁"
          >
            <ChevronLeft className="w-4 h-4" />
          </motion.button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1 mx-2">
            {generatePageNumbers(currentPage, totalPages).map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="w-9 h-9 flex items-center justify-center text-muted-foreground">
                  ⋯
                </span>
              ) : (
                <motion.button
                  key={page}
                  whileTap={tapScale}
                  onClick={() => onPageChange(page as number)}
                  className={cn(
                    "w-9 h-9 rounded-xl flex items-center justify-center text-sm font-medium transition-all",
                    currentPage === page
                      ? "bg-gold text-background shadow-glow-gold"
                      : "glass-card hover:border-gold/30 text-muted-foreground hover:text-gold"
                  )}
                >
                  {page}
                </motion.button>
              )
            ))}
          </div>

          {/* Next */}
          <motion.button
            whileTap={canGoNext ? tapScale : undefined}
            onClick={() => canGoNext && onPageChange(currentPage + 1)}
            disabled={!canGoNext}
            className={cn(
              "w-9 h-9 rounded-xl flex items-center justify-center transition-all",
              canGoNext
                ? "glass-card hover:border-gold/30 text-muted-foreground hover:text-gold"
                : "bg-white/5 text-white/20 cursor-not-allowed"
            )}
            aria-label="下一頁"
          >
            <ChevronRight className="w-4 h-4" />
          </motion.button>

          {/* Last */}
          <motion.button
            whileTap={canGoNext ? tapScale : undefined}
            onClick={() => canGoNext && onPageChange(totalPages)}
            disabled={!canGoNext}
            className={cn(
              "w-9 h-9 rounded-xl flex items-center justify-center transition-all",
              canGoNext
                ? "glass-card hover:border-gold/30 text-muted-foreground hover:text-gold"
                : "bg-white/5 text-white/20 cursor-not-allowed"
            )}
            aria-label="最後一頁"
          >
            <ChevronsRight className="w-4 h-4" />
          </motion.button>
        </div>
      )}
    </div>
  )
}

// Helper function to generate page numbers with ellipsis
function generatePageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const pages: (number | '...')[] = []

  if (current <= 3) {
    // Near start: 1 2 3 4 ... last
    for (let i = 1; i <= 4; i++) pages.push(i)
    pages.push('...')
    pages.push(total)
  } else if (current >= total - 2) {
    // Near end: 1 ... last-3 last-2 last-1 last
    pages.push(1)
    pages.push('...')
    for (let i = total - 3; i <= total; i++) pages.push(i)
  } else {
    // Middle: 1 ... current-1 current current+1 ... last
    pages.push(1)
    pages.push('...')
    pages.push(current - 1)
    pages.push(current)
    pages.push(current + 1)
    pages.push('...')
    pages.push(total)
  }

  return pages
}

// Hook for pagination state management
export function usePagination<T>(items: T[], initialPageSize = 10) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)

  const totalItems = items.length
  const totalPages = Math.ceil(totalItems / pageSize)

  // Reset to page 1 when page size changes
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setCurrentPage(1)
  }

  // Ensure current page is valid
  const validCurrentPage = Math.min(currentPage, Math.max(1, totalPages))
  if (validCurrentPage !== currentPage) {
    setCurrentPage(validCurrentPage)
  }

  const startIndex = (validCurrentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedItems = items.slice(startIndex, endIndex)

  return {
    currentPage: validCurrentPage,
    pageSize,
    totalPages,
    totalItems,
    paginatedItems,
    setCurrentPage,
    setPageSize: handlePageSizeChange,
  }
}

// Need to import useState for the hook
import { useState } from 'react'
